import React, { useMemo } from "react";
import { PhoneSupportCode } from "@ui/support-chat";
import type { HttpAdapter } from "@ui/support-chat";
import api from "../../api/axios";
import {
  isPhoneSupportEnabled,
  PHONE_SUPPORT_NUMBER,
} from "../../features/phoneSupport";

/**
 * Dashboard card that issues a code for authenticating on a phone call.
 *
 * Renders nothing unless the feature is switched on — see
 * `features/phoneSupport.ts` for the env var and the per-browser override.
 *
 * Auth rides the shared axios instance, whose interceptor attaches the
 * bearer token, so the code is always minted for the signed-in user.
 */
const PhoneSupportCard: React.FC = () => {
  const http: HttpAdapter = useMemo(
    () => ({
      get: async <T,>(url: string): Promise<T> => (await api.get<T>(url)).data,
      post: async <T,>(url: string, body?: unknown): Promise<T> =>
        (await api.post<T>(url, body ?? {})).data,
    }),
    [],
  );

  if (!isPhoneSupportEnabled()) return null;

  return (
    <div className="mb-6 rounded-xl bg-white p-5 shadow-sm">
      <h2 className="mb-3 text-lg font-semibold text-primary">
        Phone support
      </h2>
      <PhoneSupportCode http={http} phoneNumber={PHONE_SUPPORT_NUMBER} />
    </div>
  );
};

export default PhoneSupportCard;
